<template>
  <div class="accesses">
    <vtitle />

    <div class="accesses__body">
      <div v-if="forbidden">
        <div class="forbidden">
          <i class="fa fa-exclamation icon" aria-hidden></i>
        </div>
      </div>

      <div v-else>
        <div v-if="allowManage" class="standart-navigation">
          <div class="row-block actions">
            <div class="filters">
              <multiselect
                v-model="newUser"
                v-validate="'required'"
                class="boxy"
                type="text"
                :style="{ borderColor: errors.has('newUser') ? 'red' : '' }"
                :options="companyUsers"
                :show-labels="false"
                :show-no-results="false"
                :label="!is_japan_locale ? 'computed_name' : 'computed_name_reverse'"
                track-by="name"
                :placeholder="$tr('vacancy.preferences.accesses.placeholder')"
              />
            </div>

            <div class="actions-panel">
              <div class="buttons-group">
                <vbutton
                  label="common.add"
                  color="grey"
                  :disabled="(newUser ? false : true) || !allowAddUser"
                  @click="addUser"
                />

                <vbutton
                  v-tooltip.top-center="$tr('common.iconTooltips.removeAccessSelectedUsers')"
                  label="settings.managers.deleteUser.delete"
                  color="transparent"
                  text_color="red"
                  icon="icon-bin-red"
                  :disabled="!allowBulkActions"
                  @click="handleRemoving"
                />
              </div>
            </div>
          </div>
        </div>

        <users-list :list="users" :loading="loading" :allow-manage="allowManage" />

        <div v-if="allowManage" class="row-block button-wrapper">
          <vbutton
            v-if="userIsCompanyAdmin"
            class="flex-algin-left"
            :text="$tr('vacancy.preferences.accesses.inviteNewUser')"
            color="transparent"
            text_color="grey"
            icon="icon_plus"
            @click="setDisplayInviteModal(true)"
          />

          <vbutton
            class="vacancy-button"
            label="common.save"
            color="white"
            :loading="loadingRequest"
            :disabled="loadingRequest"
            @click="save(false)"
          />
          <vbutton
            class="vacancy-button"
            label="vacancy.saveAndContinue"
            :loading="loadingRequest"
            :disabled="loadingRequest"
            @click="save(true)"
          />
        </div>
      </div>
    </div>

    <invite-modal
      v-if="displayInviteModal"
      :is-success="isInviteModalSuccess"
      :title="$tr('admin.companies.inviteModal.userTitle')"
      :loading="loadingInvite"
      @submit="inviteUser"
      @close="setDisplayInviteModal(false)"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import axios from 'axios';
import { compact, find, orderBy } from 'lodash';
import UsersList from './components/List.vue';
import vtitle from '@/routes_new/inner/vacancy/components/vtitle.vue';
import InviteModal from '@/routes_new/inner/admin/components/InviteModal.vue';

export default {
  name: 'PreferencesAccesses',
  components: {
    UsersList,
    vtitle,
    InviteModal
  },

  data() {
    return {
      loadingRequest: false,
      displayInviteModal: false,
      isInviteModalSuccess: false,
      loadingInvite: false
    };
  },

  computed: {
    ...mapState('vacancy_new', { vacancy: 'item' }),

    userIsCompanyAdmin() {
      return this.$store.getters['user/isCompanyAdmin'];
    },

    user() {
      return this.$store.getters['user/current'];
    },

    companyId() {
      if (!this.user || !this.user.company) {
        return null;
      }
      return this.user.company.id;
    },

    // vacancy() {
    //   return this.$store.getters['vacancy/one'];
    // },

    vacancy_id() {
      if (!this.vacancy) {
        return null;
      }

      return this.vacancy.id;
    },

    loading() {
      return this.$store.getters['vacancy/loading'];
    },

    forbidden() {
      return this.$store.getters['vacancy/error'] !== null;
    },

    allowManage() {
      if (!this.vacancy || !this.vacancy.role) {
        return false;
      }

      const role = this.vacancy.role;
      if (role !== 'admin' && role !== 'role_edit_scoring') {
        return false;
      }

      return true;
    },

    is_japan_locale() {
      return this.$store.getters['locale/is_japan_locale'];
    },

    companyUsers() {
      const users = this.$store.getters['users/list'] || [];
      const permissions = this.$store.getters['vacancy/permissions'];
      const usersFilteredPermissions = users.filter(user => !find(permissions, { user: { email: user.email } }));

      const computed_name = !this.is_japan_locale ? 'computed_name' : 'computed_name_reverse';
      return orderBy(
        usersFilteredPermissions,
        [user => user.computed_name.toLowerCase()],
        [computed_name, 'email'],
        ['asc']
      );
    },

    users() {
      const permissions = this.$store.getters['vacancy/permissions'];
      const usersList = this.$store.getters['users/list'];
      let users = compact(
        permissions.map(permission => {
          if (!permission) {
            return null;
          }
          const user = find(usersList, permission.user);
          return user ? { ...user, permission: permission.id } : null;
        })
      );

      users = this.checkLastAdmin(users);

      return users;
    },

    selectedUsers() {
      return this.$store.getters['users/selectedUsers'];
    },

    allowBulkActions() {
      return this.selectedUsers ? this.selectedUsers.length > 0 : false;
    },

    newUser: {
      get() {
        return this.$store.getters['vacancy/newPermission'];
      },

      set(value) {
        this.$store.commit('vacancy/updateNewPermission', value);
      }
    },

    allowAddUser() {
      const user = this.newUser;
      return user && user.email;
    },

    numberOfSelected() {
      return this.selectedUsers ? this.selectedUsers.length : null;
    }
  },

  created() {
    this.fetch();
  },

  methods: {
    async fetch() {
      return Promise.all([
        this.$store.dispatch('users/fetchUsers'),
        this.$store.dispatch('dictionary/fetchPermissions'),
        this.$store.dispatch('vacancy/fetchPermissions', this.vacancy)
      ]);
    },

    addUser() {
      if (this.allowAddUser) {
        const { id } = this.$store.getters['dictionary/permissions'].find(permission => permission.id !== 'admin');
        const { email } = this.newUser;

        this.$store.commit('vacancy/updateNewPermission', null);
        this.$store.commit('vacancy/addPermission', { id, user: { email } });
      }
    },

    setDisplayInviteModal(val) {
      this.displayInviteModal = val;

      if (!val) {
        this.isInviteModalSuccess = false;
      }
    },

    inviteUser({ email }) {
      const userId = this.user ? this.user.id : null;

      if (!userId) {
        return;
      }
      this.loadingInvite = true;

      axios
        .post(`v2/registration/invites`, {
          company_id: this.companyId,
          email,
          user_id: userId,
          type: 'join_to_company'
        })
        .then(response => {
          const id = response.data.id;
          console.log('id: ', id);
          return axios.post(`v2/registration/invite/${id}/vacancy`, [
            {
              id: this.vacancy_id
            }
          ]);
        })
        .then(() => {
          this.isInviteModalSuccess = true;
          this.loadingInvite = false;
        })
        .catch(e => {
          this.loadingInvite = false;
          let message = '';
          if (e && e.response && e.response.data && e.response.data.email) {
            message = this.$tr(`error.${Object.keys(e.response.data.email)[0]}`);
          } else {
            message = this.$tr('common.error');
          }
          this.$noty.error(message);
        });
    },

    deleteSelected() {
      this.$store.getters['users/selectedUsers']
        .map(user => ({ id: user.permission, user: { email: user.email } }))
        .map(permission => this.$store.commit('vacancy/deletePermission', permission));
      this.$store.commit('users/resetSelected');
    },

    checkLastAdmin(users) {
      let adminsCount = 0;

      users.forEach(user => {
        if (user.permission === 'admin') {
          adminsCount += 1;
        }
      });

      users.map(user => {
        if (adminsCount === 1 && user.permission === 'admin') {
          user.isLastAdmin = true;
        } else {
          user.isLastAdmin = false;
        }
      });

      return users;
    },

    save(is_continue = 'false') {
      this.loadingRequest = true;
      this.$store.commit('vacancy/resetChangesTimestamps');
      this.$store
        .dispatch('vacancy/savePermissions')
        .then(() => {
          this.loadingRequest = false;
          this.$noty.success(this.$tr('vacancy.preferences.modals.saveSuccess.text')); // уточнить текст для сохранения настроек доступа

          if (window.gtag) {
            window.gtag({ event: 'vacancy_permission' });
          }

          if (is_continue) {
            this.$router.push({ path: `/vacancies/${this.vacancy_id}/invitation` });
          }
        })
        .catch(e => {
          this.loadingRequest = false;
          this.$noty.error(this.$tr('vacancy.preferences.modals.saveFailure.text')); // уточнить текст для ошибки сохранения настроек доступа
          throw e;
        });
    },

    handleRemoving() {
      this.deleteSelected();
    }
  }
};
</script>

<style lang="scss" scoped>
.accesses {
  max-width: 1100px;
}

.filters {
  flex: 1;
  margin-right: 10px !important;
}

.standart-navigation {
  margin-top: -12px;
  padding-bottom: 5px;
}

.standart-navigation .actions .filters .multiselect {
  width: 100%;
  max-width: 504px;
  margin-right: 0;
}

.actions-panel {
  display: flex;
  justify-content: flex-start;
  margin-left: 0;
}

.row-block.actions {
  justify-content: flex-start;
}

.buttons-group span {
  white-space: nowrap;
}
.row-block {
  margin: 25px 0;
}

.button-wrapper {
  display: flex;
  justify-content: flex-end;
  margin: 44px 0 100px;
  .vacancy-button:not(:last-child) {
    margin-right: 10px;
  }
}
.flex-algin-left {
  margin-right: auto;
}
</style>
